import React, { ReactNode, useMemo, useRef, useState } from "react"
import AutoSuggest, { AutoSuggestSuggestion } from "@rio-cloud/rio-uikit/AutoSuggest"
import Rioglyph from "@rio-cloud/rio-uikit/Rioglyph"
import { FormattedMessage } from "react-intl"
import { Asset as AccountAsset } from "../../model/asset/asset"
import { Asset as ConfirmationAsset } from "../../model/transportorder/transportOrder"
import { useAssetsOfAccount } from "../../hooks/assets"
import { FormGroupWrapper } from "../Form/FormGroupWrapper"
import Tooltip from "@rio-cloud/rio-uikit/Tooltip"
import OverlayTrigger from "@rio-cloud/rio-uikit/OverlayTrigger"
import {
    createLicensePlateAssetLookup,
    normalizeLicensePlate,
    resolveLicensePlateAsset,
} from "@/helper/licensePlateAssetResolver"

interface Props {
    value?: ConfirmationAsset
    label?: ReactNode
    onChange: (asset?: ConfirmationAsset) => void
    className?: string
    disabled?: boolean
    warnIfMissing?: boolean
}

type LicensePlateSuggestion = AutoSuggestSuggestion & {
    assetId?: string
    assetName?: string
    assetType?: string
    countryCode?: string
    displayLabel?: string
    isFreeText?: boolean
    suggestionKey?: string
}

type AutoSuggestInputPropsWithClearButton = NonNullable<React.ComponentProps<typeof AutoSuggest>["inputProps"]> & {
    showClearButton?: boolean
}

type SelectionType = "asset" | "freeText"

const toConfirmationAsset = (asset: AccountAsset): ConfirmationAsset => ({
    id: asset.id,
    licensePlate: asset.licensePlate,
    countryCode: asset.licensePlateCountryCode,
})

const displayValueForAsset = (asset: AccountAsset): string =>
    asset.licensePlate && asset.name
        ? `${asset.licensePlate} (${asset.name})`
        : asset.licensePlate || asset.name

const displayValueForSuggestion = (suggestion: LicensePlateSuggestion): string =>
    suggestion.assetId && suggestion.assetName && suggestion.displayLabel !== suggestion.assetName
        ? `${suggestion.displayLabel} (${suggestion.assetName})`
        : suggestion.displayLabel ?? suggestion.label ?? ""

const freeTextSuggestionFor = (licensePlate: string): LicensePlateSuggestion => ({
    label: licensePlate,
    isFreeText: true,
})

const iconClass = (assetType?: string): string => {
    switch (assetType?.toLowerCase()) {
    case "bus":
        return "rioglyph-bus"
    case "trailer":
        return "rioglyph-trailer"
    case "truck":
        return "rioglyph-truck"
    case "van":
        return "rioglyph-van"
    default:
        return "rioglyph-truck"
    }
}

const AssetSuccessIcon = (props: { assetType?: string, className?: string }): React.ReactElement => (
    <span className={props.className} aria-hidden>
        <Rioglyph
            icon={iconClass(props.assetType)}
            iconClassName="text-color-gray"
            pairIcon="rioglyph-ok-sign"
            pairIconClassName="text-color-success"
            size="18"
        />
    </span>
)

const VehicleIconAddon = (props: { assetType?: string, hasAssetSelection: boolean }): React.ReactElement => (
    <span className="input-group-addon width-40">
        {props.hasAssetSelection
            ? <AssetSuccessIcon assetType={props.assetType}/>
            : <span className="rioglyph rioglyph-truck" aria-hidden/>}
    </span>
)

const suggestionsFor = (assets: AccountAsset[], query?: string): LicensePlateSuggestion[] => {
    const trimmedQuery = query?.trim() ?? ""
    const normalizedQuery = normalizeLicensePlate(trimmedQuery)
    const assetSuggestions = assets
        .filter(asset =>
            normalizedQuery === ""
            || normalizeLicensePlate(asset.licensePlate ?? "").includes(normalizedQuery)
            || asset.name.toLowerCase().includes(trimmedQuery.toLowerCase())
        )
        .map(asset => ({
            displayLabel: asset.licensePlate || asset.name,
            assetId: asset.id,
            assetName: asset.name,
            assetType: asset.type,
            countryCode: asset.licensePlateCountryCode,
            suggestionKey: asset.id,
        }))
        .sort((a, b) => (a.displayLabel ?? "").localeCompare(b.displayLabel ?? ""))

    return trimmedQuery ? [...assetSuggestions, freeTextSuggestionFor(trimmedQuery)] : assetSuggestions
}

export const LicensePlateAssetSelect = (props: Props): React.ReactElement => {
    const assets = useAssetsOfAccount()
    const isSelectingSuggestion = useRef(false)
    const [searchValue, setSearchValue] = useState<string | undefined>(props.value?.licensePlate)
    const [displayValue, setDisplayValue] = useState<string | undefined>(props.value?.licensePlate)
    const [committedValue, setCommittedValue] = useState<ConfirmationAsset | undefined>(props.value)
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [selectionType, setSelectionType] = useState<SelectionType | undefined>(
        props.value ? props.value.id ? "asset" : "freeText" : undefined
    )

    const suggestions = useMemo(
        () => selectionType === undefined || isEditing ? suggestionsFor(assets, searchValue) : [],
        [assets, isEditing, searchValue, selectionType]
    )

    const licensePlateAssetLookup = useMemo(
        () => createLicensePlateAssetLookup(assets),
        [assets]
    )

    const selectedAsset = useMemo(
        () => committedValue?.id ? assets.find(asset => asset.id === committedValue.id) : undefined,
        [assets, committedValue]
    )

    const committedDisplayValue = selectionType === "asset" && selectedAsset
        ? displayValueForAsset(selectedAsset)
        : committedValue?.licensePlate

    const committedEditValue = selectionType === "asset" && selectedAsset
        ? selectedAsset.licensePlate || selectedAsset.name
        : committedValue?.licensePlate

    const effectiveDisplayValue = isEditing
        ? displayValue
        : committedDisplayValue ?? displayValue

    const setValues = (value?: string): void => {
        setDisplayValue(value)
        setSearchValue(value)
        if (selectionType !== undefined && value?.trim() !== committedEditValue) {
            setSelectionType(undefined)
        }
    }

    const selectFreeText = (licensePlate: string): void => {
        const normalized = licensePlate.trim().toUpperCase()
        setDisplayValue(normalized)
        setSearchValue(normalized)
        setCommittedValue(normalized ? { licensePlate: normalized } : undefined)
        setSelectionType(normalized ? "freeText" : undefined)
        setIsEditing(false)
        props.onChange(normalized ? { licensePlate: normalized } : undefined)
    }

    const selectAsset = (asset: AccountAsset): void => {
        const confirmationAsset = toConfirmationAsset(asset)
        setDisplayValue(displayValueForAsset(asset))
        setSearchValue(undefined)
        setCommittedValue(confirmationAsset)
        setSelectionType("asset")
        setIsEditing(false)
        props.onChange(confirmationAsset)
    }

    const startEditing = (): void => {
        const editValue = committedEditValue ?? displayValue
        setIsEditing(true)
        setDisplayValue(editValue)
        setSearchValue(editValue)
    }

    const selectOnBlur = (): void => {
        if (isSelectingSuggestion.current) {
            return
        }

        const trimmed = (displayValue ?? "").trim()
        if (selectionType !== undefined && trimmed === committedEditValue) {
            setIsEditing(false)
            setDisplayValue(committedDisplayValue ?? committedEditValue)
            setSearchValue(committedEditValue)
            return
        }

        if (!trimmed) {
            setSelectionType(undefined)
            setCommittedValue(undefined)
            setIsEditing(false)
            props.onChange(undefined)
            return
        }

        const normalized = normalizeLicensePlate(trimmed)
        const matchedAsset = resolveLicensePlateAsset(licensePlateAssetLookup, trimmed)
        const matchingAssetNames = licensePlateAssetLookup.has(normalized)
            ? []
            : assets.filter(asset => asset.name.toLowerCase() === trimmed.toLowerCase())

        if (matchedAsset) {
            selectAsset(matchedAsset)
        } else if (matchingAssetNames.length === 1) {
            selectAsset(matchingAssetNames[0])
        } else {
            selectFreeText(trimmed)
        }
    }

    const hasWarning = props.warnIfMissing && selectionType === undefined && props.value === undefined
    const hasSuccess = selectionType !== undefined && !hasWarning
    const successHint = selectionType === "freeText"
        ? <FormattedMessage id="transportOrder.confirmation.licensePlate.manual.success"/>
        : selectionType === "asset"
            ? <FormattedMessage id="transportOrder.confirmation.licensePlate.asset.success"/>
            : undefined

    const labelWithInfo = <FormattedMessageWithInfoPopover
        label={props.label}
        infoMessageId={"transportOrder.confirmation.licensePlate.manual.info"}
    />

    const clearSelection = (): void => {
        setValues(undefined)
        setCommittedValue(undefined)
        setSelectionType(undefined)
        setIsEditing(false)
        props.onChange(undefined)
    }

    const inputProps: AutoSuggestInputPropsWithClearButton = {
        onClear: clearSelection,
        value: effectiveDisplayValue ?? "",
        showClearButton: !hasSuccess,
        onFocus: startEditing,
        onBlur: selectOnBlur,
        disabled: props.disabled,
        placeholder: "Enter license plate or search vehicle",
    }

    const autoSuggest = <AutoSuggest
        inputProps={inputProps}
        leadingInputAddons={[
            <VehicleIconAddon
                key="vehicle-icon"
                assetType={selectedAsset?.type}
                hasAssetSelection={selectionType === "asset"}
            />
        ]}
        suggestions={suggestions}
        noItemMessage={<FormattedMessage id="transportOrder.suggestions.no.asset.available"/>}
        onSuggestionsFetchRequested={({ value }): void => setValues(value)}
        onSuggestionSelected={(_, selectedSuggestion): void => {
            const suggestion = selectedSuggestion.suggestion as LicensePlateSuggestion
            isSelectingSuggestion.current = false
            const selectedAsset = suggestion.assetId ? assets.find(asset => asset.id === suggestion.assetId) : undefined
            if (selectedAsset) {
                selectAsset(selectedAsset)
            } else if (suggestion.label) {
                selectFreeText(suggestion.label)
            }
        }}
        getSuggestionValue={(suggestion: LicensePlateSuggestion): string => displayValueForSuggestion(suggestion)}
        customSuggestionKey="suggestionKey"
        renderSuggestion={(suggestion: AutoSuggestSuggestion): React.ReactElement => {
            const licensePlateSuggestion = suggestion as LicensePlateSuggestion
            if (licensePlateSuggestion.assetId) {
                return (
                    <div
                        className="display-flex align-items-start gap-10 padding-y-3"
                        onMouseDown={(): void => {
                            isSelectingSuggestion.current = true
                        }}
                    >
                        <AssetSuccessIcon assetType={licensePlateSuggestion.assetType} className="margin-top-2"/>
                        <span className="display-flex flex-column line-height-125rel">
                            <span className="text-medium">{licensePlateSuggestion.displayLabel}</span>
                            {licensePlateSuggestion.assetName !== licensePlateSuggestion.displayLabel
                                ? <span className="text-color-gray text-size-12">{licensePlateSuggestion.assetName}</span>
                                : null}
                        </span>
                    </div>
                )
            }
            return (
                <div
                    className="display-flex align-items-start gap-10 padding-y-3"
                    onMouseDown={(): void => {
                        isSelectingSuggestion.current = true
                    }}
                >
                    <span className="rioglyph rioglyph-truck text-color-gray text-size-18 margin-top-2" aria-hidden/>
                    <span className="display-flex flex-column line-height-125rel">
                        <span className="text-medium">
                            <FormattedMessage
                                id="transportOrder.confirmation.licensePlate.manualSuggestion.label"
                                values={{ licensePlate: licensePlateSuggestion.label }}
                            />
                        </span>
                        <span className="text-color-gray text-size-12">
                            <FormattedMessage id="transportOrder.confirmation.licensePlate.manualSuggestion.hint"/>
                        </span>
                    </span>
                </div>
            )
        }}
    />

    return (
        <div className={props.className} data-testid="license-plate-asset-select">
            <FormGroupWrapper
                label={labelWithInfo}
                component={autoSuggest}
                hasWarning={hasWarning}
                hasSuccess={hasSuccess}
                feedbackIconAriaLabel={hasSuccess ? "Clear transport vehicle" : undefined}
                feedbackIconClassName={hasSuccess ? "rioglyph-remove-sign text-color-gray" : undefined}
                feedbackIconStyle={hasSuccess ? { right: -2 } : undefined}
                onFeedbackIconClick={hasSuccess ? clearSelection : undefined}
                hint={hasSuccess
                    ? successHint
                    : hasWarning ? <FormattedMessage id="transportOrder.confirmation.no.asset.selected"/> : undefined}
            />
        </div>
    )
}
const FormattedMessageWithInfoPopover = (props: {
    label: ReactNode
    infoMessageId: string
}) => {
    const tooltip = <Tooltip id="tooltip" allowOnTouch width={300} textAlignment={"left"}>
        <FormattedMessage id={props.infoMessageId} />
    </Tooltip>

    return <OverlayTrigger
        placement={"top"}
        overlay={tooltip}>
        <div>
            {props.label}
            <span className="rioglyph rioglyph-info-sign margin-left-5 text-color-info" />
        </div>
    </OverlayTrigger>
}
